import { useState, useEffect, useRef, useCallback } from 'react';
import { academyAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
    FileText, Image as ImageIcon, FileSpreadsheet, FileArchive, File as FileIcon,
    Eye, Download, Trash2, UploadCloud, Loader2, Paperclip
} from 'lucide-react';

const VIEWABLE = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'];
const ACCEPT = '.pdf,.jpg,.jpeg,.png,.gif,.webp,.svg,.bmp,.heic,.heif,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.csv,.txt';
const MAX_SIZE = 25 * 1024 * 1024;

const iconFor = (ext) => {
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'heic', 'heif'].includes(ext)) return ImageIcon;
    if (ext === 'pdf') return FileText;
    if (['xls', 'xlsx', 'csv'].includes(ext)) return FileSpreadsheet;
    if (ext === 'zip') return FileArchive;
    return FileIcon;
};
const colorFor = (ext) => {
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'heic', 'heif'].includes(ext)) return 'text-purple-600 bg-purple-50';
    if (ext === 'pdf') return 'text-red-600 bg-red-50';
    if (['xls', 'xlsx', 'csv'].includes(ext)) return 'text-emerald-600 bg-emerald-50';
    if (ext === 'zip') return 'text-amber-600 bg-amber-50';
    if (['doc', 'docx'].includes(ext)) return 'text-blue-600 bg-blue-50';
    return 'text-slate-600 bg-slate-100';
};
const fmtSize = (b) => {
    if (!b && b !== 0) return '';
    if (b < 1024) return `${b} o`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} Ko`;
    return `${(b / (1024 * 1024)).toFixed(1)} Mo`;
};

export const ModuleResources = ({ moduleId, canManage = false }) => {
    const [resources, setResources] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [dragOver, setDragOver] = useState(false);
    const [busyId, setBusyId] = useState(null);
    const inputRef = useRef(null);

    const load = useCallback(async () => {
        try {
            const res = await academyAPI.getModuleResources(moduleId);
            setResources(res.data || []);
        } catch {
            setResources([]);
        } finally {
            setLoading(false);
        }
    }, [moduleId]);

    useEffect(() => { load(); }, [load]);

    const handleFiles = async (fileList) => {
        const files = Array.from(fileList || []);
        if (!files.length) return;
        for (const f of files) {
            if (f.size > MAX_SIZE) {
                toast.error(`"${f.name}" dépasse 25 Mo`);
                continue;
            }
            setUploading(true);
            setProgress(0);
            try {
                const fd = new FormData();
                fd.append('file', f);
                await academyAPI.uploadModuleResource(moduleId, fd, (e) => {
                    if (e.total) setProgress(Math.round((e.loaded * 100) / e.total));
                });
                toast.success(`"${f.name}" ajouté`);
            } catch (err) {
                toast.error(err?.response?.data?.detail || `Échec de l'envoi de "${f.name}"`);
            }
        }
        setUploading(false);
        setProgress(0);
        if (inputRef.current) inputRef.current.value = '';
        load();
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        if (canManage && !uploading) handleFiles(e.dataTransfer.files);
    };

    const openFile = async (res, download) => {
        setBusyId(res.id + (download ? '-dl' : '-view'));
        try {
            const r = await academyAPI.getResourceFile(moduleId, res.id, download ? 1 : 0);
            const blob = new Blob([r.data], { type: res.content_type || 'application/octet-stream' });
            const url = URL.createObjectURL(blob);
            if (download) {
                const a = document.createElement('a');
                a.href = url;
                a.download = res.original_name;
                document.body.appendChild(a);
                a.click();
                a.remove();
            } else {
                window.open(url, '_blank', 'noopener');
            }
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch {
            toast.error('Impossible d\'ouvrir le fichier');
        } finally {
            setBusyId(null);
        }
    };

    const remove = async (res) => {
        if (!window.confirm(`Supprimer "${res.original_name}" ?`)) return;
        setBusyId(res.id + '-del');
        try {
            await academyAPI.deleteModuleResource(moduleId, res.id);
            toast.success('Fichier supprimé');
            load();
        } catch {
            toast.error('Échec de la suppression');
        } finally {
            setBusyId(null);
        }
    };

    if (loading) {
        return <div className="flex items-center gap-2 text-slate-400 text-sm py-4"><Loader2 className="h-4 w-4 animate-spin" /> Chargement des supports…</div>;
    }

    // Côté apprenant : ne rien afficher si aucun support et pas de gestion
    if (!canManage && resources.length === 0) return null;

    return (
        <div data-testid="module-resources" className="rounded-2xl border-2 border-indigo-100 bg-gradient-to-br from-indigo-50/70 to-white p-5 sm:p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-1">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-600 text-white shadow-sm">
                    <Paperclip className="h-5 w-5" />
                </span>
                <div>
                    <h3 className="text-lg font-bold text-slate-900">Trames &amp; supports</h3>
                    <p className="text-xs text-slate-500">Documents à visionner et télécharger pour ce module</p>
                </div>
                {resources.length > 0 && (
                    <span className="ml-auto text-xs font-semibold text-indigo-700 bg-indigo-100 px-2.5 py-1 rounded-full">
                        {resources.length} fichier{resources.length > 1 ? 's' : ''}
                    </span>
                )}
            </div>

            {/* Zone drag & drop (admin) */}
            {canManage && (
                <div
                    data-testid="resource-dropzone"
                    onClick={() => !uploading && inputRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={onDrop}
                    className={`mt-3 mb-4 cursor-pointer rounded-xl border-2 border-dashed px-4 py-6 text-center transition-colors ${dragOver ? 'border-indigo-500 bg-indigo-50' : 'border-indigo-200 hover:border-indigo-400 hover:bg-indigo-50/50'}`}
                >
                    <input
                        ref={inputRef}
                        type="file"
                        multiple
                        accept={ACCEPT}
                        className="hidden"
                        data-testid="resource-file-input"
                        onChange={(e) => handleFiles(e.target.files)}
                    />
                    {uploading ? (
                        <div className="flex flex-col items-center gap-2">
                            <Loader2 className="h-6 w-6 text-indigo-600 animate-spin" />
                            <p className="text-sm text-indigo-700 font-medium">Envoi en cours… {progress}%</p>
                            <div className="w-full max-w-xs h-1.5 bg-indigo-100 rounded-full overflow-hidden">
                                <div className="h-full bg-indigo-600 transition-all" style={{ width: `${progress}%` }} />
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-col items-center gap-1.5">
                            <UploadCloud className="h-7 w-7 text-indigo-500" />
                            <p className="text-sm font-medium text-slate-700">Glissez vos fichiers ici ou cliquez</p>
                            <p className="text-xs text-slate-400">PDF, images, Word, Excel, PowerPoint, ZIP — 25 Mo max</p>
                        </div>
                    )}
                </div>
            )}

            {/* Liste des fichiers */}
            {resources.length === 0 ? (
                !canManage ? null : <p className="text-sm text-slate-400 text-center py-2">Aucun support pour l'instant.</p>
            ) : (
                <div className="grid sm:grid-cols-2 gap-3">
                    {resources.map((res) => {
                        const Icon = iconFor(res.ext);
                        const viewable = VIEWABLE.includes(res.ext);
                        return (
                            <div
                                key={res.id}
                                data-testid={`resource-item-${res.id}`}
                                className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 hover:shadow-md transition-shadow"
                            >
                                <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${colorFor(res.ext)}`}>
                                    <Icon className="h-5 w-5" />
                                </span>
                                <div className="min-w-0 flex-1">
                                    <p className="truncate text-sm font-semibold text-slate-800" title={res.original_name}>{res.original_name}</p>
                                    <p className="text-xs text-slate-400 uppercase">{res.ext} · {fmtSize(res.size)}</p>
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                    {viewable && (
                                        <Button
                                            size="icon" variant="ghost" className="h-8 w-8 text-indigo-600 hover:bg-indigo-50"
                                            title="Visionner" data-testid={`resource-view-${res.id}`}
                                            disabled={busyId === res.id + '-view'}
                                            onClick={() => openFile(res, false)}
                                        >
                                            {busyId === res.id + '-view' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                                        </Button>
                                    )}
                                    <Button
                                        size="icon" variant="ghost" className="h-8 w-8 text-slate-600 hover:bg-slate-100"
                                        title="Télécharger" data-testid={`resource-download-${res.id}`}
                                        disabled={busyId === res.id + '-dl'}
                                        onClick={() => openFile(res, true)}
                                    >
                                        {busyId === res.id + '-dl' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                                    </Button>
                                    {canManage && (
                                        <Button
                                            size="icon" variant="ghost" className="h-8 w-8 text-red-500 hover:bg-red-50"
                                            title="Supprimer" data-testid={`resource-delete-${res.id}`}
                                            disabled={busyId === res.id + '-del'}
                                            onClick={() => remove(res)}
                                        >
                                            {busyId === res.id + '-del' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default ModuleResources;
